import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';
import ReferralTable from '@/components/ReferralTable';
import { ReferralData, getAllReferrals, updateReferralStatus } from '@/services/firebase';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const Admin = () => {
  const [referrals, setReferrals] = useState<ReferralData[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  const fetchReferrals = useCallback(async (lastDoc = null) => {
    try {
      setLoading(true);
      
      const result = await getAllReferrals(lastDoc);
      const fetchedReferrals = result.referrals;
      const newLastVisible = result.lastVisible;
      
      setReferrals(fetchedReferrals);
      setLastVisible(newLastVisible);
      setHasMore(fetchedReferrals.length === 20); // Assuming pageSize is 20
    } catch (error) {
      console.error("Error fetching referrals:", error);
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);
  
  const handleUpdateStatus = async (id: string, status: ReferralData['status'], commission?: number) => {
    try {
      await updateReferralStatus(id, status, commission);
      
      // Update local state
      setReferrals(prev => 
        prev.map(referral => 
          referral.id === id 
            ? { ...referral, status, commission: commission !== undefined ? commission : referral.commission } 
            : referral
        )
      );
      
      toast.success(`Referral status updated to ${status}`);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update referral status");
    }
  };
  
  const handleNextPage = async () => {
    if (!lastVisible) return;
    
    try {
      await fetchReferrals(lastVisible);
      setCurrentPage(prev => prev + 1);
    } catch (error) {
      console.error("Error fetching next page:", error);
      toast.error("Failed to load more referrals");
    }
  };
  
  const handlePreviousPage = async () => {
    if (currentPage === 1) return;
    
    try {
      // In a real app, you'd store previous pages' lastVisible items
      // For this demo, we're just going back to page 1
      await fetchReferrals(null);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching previous page:", error);
      toast.error("Failed to load previous referrals");
    }
  };
  
  // Calculate some stats for the admin dashboard
  const calculateStats = () => {
    if (referrals.length === 0) return null;
    
    const total = referrals.length;
    const byStatus = {
      new: referrals.filter(r => r.status === 'new').length,
      contacted: referrals.filter(r => r.status === 'contacted').length,
      inProgress: referrals.filter(r => r.status === 'in-progress').length,
      sold: referrals.filter(r => r.status === 'sold').length,
      cancelled: referrals.filter(r => r.status === 'cancelled').length,
    };
    
    const totalCommission = referrals
      .filter(r => r.status === 'sold' && r.commission)
      .reduce((sum, r) => sum + (r.commission || 0), 0);
    
    return { total, byStatus, totalCommission };
  };
  
  const stats = calculateStats();
  
  return (
    <ProtectedRoute adminOnly>
      <Layout>
        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
            <p className="text-muted-foreground">
              Manage and track all referrals across the system
            </p>
          </div>
          
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fadeIn">
              <Card className="glass-panel">
                <CardHeader className="pb-2">
                  <CardTitle>Total Referrals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats.total}</div>
                </CardContent>
              </Card>
              
              <Card className="glass-panel">
                <CardHeader className="pb-2">
                  <CardTitle>Pending Review</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-yellow-500">
                    {stats.byStatus.new}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-panel">
                <CardHeader className="pb-2">
                  <CardTitle>Sold Referrals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-500">
                    {stats.byStatus.sold}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-panel">
                <CardHeader className="pb-2">
                  <CardTitle>Total Commissions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gradient">
                    ${stats.totalCommission.toFixed(2)}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">All Referrals</h2>
            
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-pulse text-xl">Loading referrals...</div>
              </div>
            ) : (
              <ReferralTable 
                referrals={referrals}
                isAdmin={true}
                onUpdateStatus={handleUpdateStatus}
                pagination={{
                  currentPage,
                  hasMore,
                  onNext: handleNextPage,
                  onPrevious: handlePreviousPage
                }}
              />
            )}
          </div>
        </div>
      </Layout>
    </ProtectedRoute>
  );
};

export default Admin;
